import React, { useState } from 'react';
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Treemap, ResponsiveContainer } from 'recharts';
import { Target, Award, Zap } from 'lucide-react';
import { ChartCard, MetricCard, EmptyState, COLORS, formatRevenue } from './common.jsx';
import GameDetailsModal from '../../discovery/GameDetailsModal';

const OpportunitiesTab = ({ data }) => {
    const {
        acquisitionTargets = { topTargets: [] },
        indieGems = { topGems: [] },
        marketOpportunities = []
    } = data || {};
    const [selectedGame, setSelectedGame] = useState(null);
    const [detailsCache, setDetailsCache] = useState({});

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard
                    icon={<Target className="h-8 w-8" />}
                    title="Acquisition Targets"
                    value={(acquisitionTargets.topTargets || []).length}
                    subtitle="High-value opportunities"
                    color="blue"
                />
                <MetricCard
                    icon={<Award className="h-8 w-8" />}
                    title="Indie Gems"
                    value={(indieGems.topGems || []).length}
                    subtitle="Undervalued titles"
                    color="purple"
                />
                <MetricCard
                    icon={<Zap className="h-8 w-8" />}
                    title="Market Gaps"
                    value={marketOpportunities.length || 0}
                    subtitle="Low competition genres"
                    color="orange"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ChartCard title="🎯 Top Acquisition Targets" subtitle="Scored by revenue, engagement, and quality">
                    {(acquisitionTargets.topTargets || []).length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {acquisitionTargets.topTargets.slice(0, 10).map((target, index) => (
                                <div
                                    key={target.steamId || index}
                                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                                    onClick={() => setSelectedGame(target)}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-lg font-bold text-blue-600">#{index + 1}</span>
                                                <h4 className="font-bold text-gray-900">{target.name || 'Unknown'}</h4>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {(target.genres || []).slice(0, 2).join(', ') || '—'}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <div className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                                                Score: {Math.round(target.acquisitionScore || 0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Revenue</p>
                                            <p className="font-semibold">{target.revenue != null ? formatRevenue(target.revenue) : '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Score</p>
                                            <p className="font-semibold">{target.score != null ? target.score : '—'}/100</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Reviews</p>
                                            <p className="font-semibold">{target.reviews != null ? target.reviews.toLocaleString() : '—'}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                        <p>Genres: {(target.genres || []).slice(0, 3).join(', ') || '—'}</p>
                                        <p>Publisher: {(target.publishers || ['Unknown'])[0]}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No acquisition targets available" />
                    )}
                </ChartCard>

                <ChartCard title="💎 Hidden Indie Gems" subtitle="High review scores & passion, low revenue">
                    {(indieGems.topGems || []).length > 0 ? (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {indieGems.topGems.slice(0, 10).map((gem, index) => (
                                <div
                                    key={gem.steamId || index}
                                    className="p-4 border border-gray-200 rounded-lg hover:border-purple-400 hover:shadow-md transition-all cursor-pointer"
                                    onClick={() => setSelectedGame(gem)}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-lg font-bold text-purple-600">#{index + 1}</span>
                                                <h4 className="font-bold text-gray-900">{gem.name || 'Unknown'}</h4>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {(gem.genres || []).slice(0, 2).join(', ') || '—'}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <div className="px-2 py-1 bg-blue-50 text-blue-800 rounded-full text-xs font-semibold">
                                                Gem Score: {Math.round(gem.gemScore || 0)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                                        <div>
                                            <p className="text-gray-500">Revenue</p>
                                            <p className="font-semibold">{gem.revenue != null ? formatRevenue(gem.revenue) : '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Reviews</p>
                                            <p className="font-semibold">{gem.reviews != null ? gem.reviews.toLocaleString() : '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500">Wishlists</p>
                                            <p className="font-semibold">{gem.wishlists != null ? gem.wishlists.toLocaleString() : '—'}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                        <p>Genres: {(gem.genres || []).slice(0, 3).join(', ') || '—'}</p>
                                        <p>Publisher: {(gem.publishers || ['Unknown'])[0]}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No indie gems found" />
                    )}
                </ChartCard>
            </div>

            <ChartCard title="📊 Market Opportunities" subtitle="Underserved genres with revenue potential" fullWidth>
                {marketOpportunities && marketOpportunities.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                        <Treemap
                            data={marketOpportunities.slice(0, 12).map(opp => ({
                                name: opp.genre || opp.name || 'Other',
                                size: opp.totalRevenue || 1,
                                gameCount: opp.gameCount,
                                saturation: opp.saturationScore
                            }))}
                            dataKey="size"
                            aspectRatio={4 / 3}
                            stroke="#fff"
                            fill={COLORS.primary}
                        >
                            <Tooltip formatter={(value, name) => {
                                if (name === 'size') return formatRevenue(value);
                                return value;
                            }} />
                        </Treemap>
                    </ResponsiveContainer>
                ) : (
                    <EmptyState message="No market opportunities identified" />
                )}
            </ChartCard>

            {selectedGame && (
                <GameDetailsModal
                    game={selectedGame}
                    onClose={() => setSelectedGame(null)}
                    detailsCache={detailsCache}
                    setDetailsCache={setDetailsCache}
                />
            )}
        </div>
    );
};

export default OpportunitiesTab;
